import { useState, useMemo, useEffect } from "react";
import { useTranslate } from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Link as MuiLink,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { getCostoPlatam, type TCostoPlatamResponse } from "@/api/customs/textiles";
import * as XLSX from "xlsx";
import { PURCHASES } from "@/config/resources";

interface CostoPlatamTableProps {
  month: string;
}

export const CostoPlatamTable = ({ month }: CostoPlatamTableProps) => {
  const translate = useTranslate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TCostoPlatamResponse | null>(null);

  const fetchData = async () => {
    if (!month) {
      setError("Por favor selecciona un mes");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getCostoPlatam({ month });
      setData(response);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Error al cargar los datos"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [month]);

  const formatCurrency = (value: number, decimals: number = 0) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const handleExportToExcel = () => {
    if (!data) return;

    const exportData = data.detail.map((item) => ({
      "Pedido Seeri": item.purchase_number,
      "Pedido Platam": item.payment_id,
      Cliente: item.client,
      "Monto Inicial": item.initial_amount,
      "Monto Final": item.final_amount,
      Tasa: item.discount_rate,
      Descuento: item.discount,
    }));

    // Add total cost at the end
    exportData.push({
      "Pedido Seeri": "",
      "Pedido Platam": "" as any,
      Cliente: "",
      "Monto Inicial": "" as any,
      "Monto Final": "" as any,
      Tasa: "" as any,
      Descuento: "" as any,
    });
    exportData.push({
      "Pedido Seeri": "COSTO TOTAL",
      "Pedido Platam": "" as any,
      Cliente: "",
      "Monto Inicial": "" as any,
      "Monto Final": data.total_cost,
      Tasa: "" as any,
      Descuento: "" as any,
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Costo Platam");

    const date = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `costo_platam_${month}_${date}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info">
        No hay datos disponibles para el mes seleccionado
      </Alert>
    );
  }

  return (
    <Box>
      {/* Tarjeta con el total_cost */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {translate("resources.textiles.fields.costo_total")}
          </Typography>
          <Typography variant="h3" color="primary" fontWeight="bold">
            {formatCurrency(data.total_cost, 2)}
          </Typography>
        </CardContent>
      </Card>

      {/* Botón de descarga */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportToExcel}
          color="primary"
        >
          Descargar Excel
        </Button>
      </Box>

      {/* Tabla con el detalle */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {translate("resources.textiles.fields.pedido_seeri")}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" fontWeight="bold">
                  {translate("resources.textiles.fields.pedido_platam")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {translate("resources.textiles.fields.cliente")}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" fontWeight="bold">
                  {translate("resources.textiles.fields.monto_inicial")}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" fontWeight="bold">
                  {translate("resources.textiles.fields.monto_final")}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" fontWeight="bold">
                  {translate("resources.textiles.fields.tasa")}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" fontWeight="bold">
                  {translate("resources.textiles.fields.descuento")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.detail.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  <MuiLink
                    href={`/${PURCHASES}/${item.purchase_id}/show`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {item.purchase_number}
                  </MuiLink>
                </TableCell>
                <TableCell align="right">{item.payment_id}</TableCell>
                <TableCell>{item.client}</TableCell>
                <TableCell align="right">
                  {formatCurrency(item.initial_amount)}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(item.final_amount)}
                </TableCell>
                <TableCell align="right">
                  {formatPercentage(item.discount_rate)}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(item.discount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {data.detail.length === 0 && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography color="text.secondary">
            No hay detalles disponibles para este mes
          </Typography>
        </Box>
      )}
    </Box>
  );
};
